'use client';

// The `voice_control` tool's card (BUILD 2 C2-7). Its result is a client
// effect (stop, repeat, exit, rate), applied once, and only for a tool call in
// the reply to the turn this client last sent in its open voice session (the
// controller holds that reply's message id; `stopWhen` ends the run at this
// call, so "still running" would miss it). A history reload, a stream resumed
// on another device or a re-render never replays it. The effect comes from the model's
// tool call, never from the transcript (GR4). The card says, in one quiet
// line, what the call asked for.
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import { useAppCatalogLocale } from '@/i18n/context';
import voiceCopy, { type VoiceCopyKey } from '@/i18n/ns/voice';
import { useAuiState } from '@assistant-ui/react';
import { useEffect } from 'react';
import type { VoiceControlResult } from './contracts';
import { voiceController } from './runtime';

/** voice-structural: the tool's `action` to the card's line. */
const ACTION_LINES: Record<VoiceControlResult['action'], VoiceCopyKey> = {
  stop: 'controlStop',
  repeat: 'controlRepeat',
  exit: 'controlExit',
  rate: 'controlRate',
};

/** The result, checked structurally; null for anything else (an error, a partial call). */
export function parseVoiceControlResult(result: unknown): VoiceControlResult | null {
  const value = result as Partial<VoiceControlResult> | null;
  if (!value || typeof value !== 'object' || value.ok !== true) return null;
  if (typeof value.action !== 'string' || !Object.hasOwn(ACTION_LINES, value.action)) return null;
  return {
    ok: true,
    action: value.action,
    ...(typeof value.messageId === 'string' ? { messageId: value.messageId } : {}),
    ...(typeof value.rate === 'number' && Number.isFinite(value.rate) ? { rate: value.rate } : {}),
  };
}

/** The message this call belongs to (null outside a message, e.g. a test render). */
const useMessageId = (): string | null =>
  useAuiState((state) => {
    try {
      return state.message.id;
    } catch {
      return null;
    }
  });

export function VoiceControlCard({ toolCallId, result }: { toolCallId?: string; result: unknown }) {
  const copy = useNamespaceCopy(voiceCopy);
  const locale = useAppCatalogLocale();
  const control = parseVoiceControlResult(result);
  const messageId = useMessageId();
  useEffect(() => {
    if (control && toolCallId) voiceController().applyControl(toolCallId, control, messageId);
  }, [toolCallId, control?.action, control?.rate, control?.messageId, messageId]);
  if (!control) return null;
  return (
    <p
      data-slot="voice-control"
      data-action={control.action}
      className="text-muted-foreground text-xs"
    >
      {formatCopy(copy[ACTION_LINES[control.action]], {
        rate: control.rate === undefined ? '' : new Intl.NumberFormat(locale).format(control.rate),
      })}
    </p>
  );
}
